/**
 * <s-lftg-fields> — "Love for the Game" customization fields + required certification.
 * - Keeps typed text (every [data-lftg-persist] field) and the checkbox state across
 *   variant changes (the product-form partial is re-rendered on every option change).
 * - Shows the store's own error message and blocks Add to Cart while unchecked.
 *   Required fields use native `required`, so the browser also refuses to submit.
 */
const state = new Map();
const logoFiles = new Map();

class LftgFields extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const key = this.dataset.productId;
    const back = this.querySelector('[data-lftg-back]');
    const certify = this.querySelector('[data-lftg-certify]');
    const count = this.querySelector('[data-lftg-count]');
    const error = this.querySelector('[data-lftg-error]');
    const message = this.dataset.errorMessage || '';
    const fields = [...this.querySelectorAll('[data-lftg-persist]')];
    const form = this.closest('form');
    if (!certify) return;

    const saved = state.get(key);
    if (saved) {
      for (const field of fields) {
        if (field.name in saved.values) field.value = saved.values[field.name];
      }
      certify.checked = saved.checked;
    }

    const save = () => {
      const values = {};
      for (const field of fields) values[field.name] = field.value;
      state.set(key, { values, checked: certify.checked });
    };
    const updateCount = () => { if (count && back) count.textContent = String(back.value.length); };
    const sync = () => {
      certify.setCustomValidity(certify.checked ? '' : message);
      if (certify.checked && error) error.hidden = true;
    };

    updateCount();
    sync();

    for (const field of fields) {
      field.addEventListener('input', () => { updateCount(); save(); }, opts);
    }
    certify.addEventListener('change', () => { sync(); save(); }, opts);
    certify.addEventListener('invalid', () => { if (error) error.hidden = false; }, opts);

    // Optional logo upload for the "O" (customized display only).
    const logoBox = this.querySelector('[data-lftg-logo]');
    const logoInput = this.querySelector('[data-lftg-logo-input]');
    const logoPreview = this.querySelector('[data-lftg-logo-preview]');
    const logoThumb = this.querySelector('[data-lftg-logo-thumb]');
    const logoName = this.querySelector('[data-lftg-logo-name]');
    const logoRemove = this.querySelector('[data-lftg-logo-remove]');
    const logoError = this.querySelector('[data-lftg-logo-error]');
    const MAX_BYTES = 10 * 1024 * 1024;
    let logoUrl = '';

    const showLogo = (file) => {
      if (logoUrl) URL.revokeObjectURL(logoUrl);
      logoUrl = '';
      if (!file) {
        if (logoPreview) logoPreview.hidden = true;
        return;
      }
      logoUrl = URL.createObjectURL(file);
      if (logoThumb) logoThumb.src = logoUrl;
      if (logoName) logoName.textContent = file.name;
      if (logoPreview) logoPreview.hidden = false;
    };
    const clearLogo = () => {
      if (logoInput) logoInput.value = '';
      logoFiles.delete(key);
      showLogo(null);
    };
    const logoProblem = (file) => {
      if (!file) return '';
      const okType = /^image\/(png|jpeg)$/.test(file.type) || /\.(png|jpe?g)$/i.test(file.name);
      if (!okType) return logoBox?.dataset.typeError || '';
      if (file.size > MAX_BYTES) return logoBox?.dataset.sizeError || '';
      return '';
    };

    if (logoInput) {
      // Restore a file chosen before the variant change re-rendered this block.
      const savedFile = logoFiles.get(key);
      if (savedFile && typeof DataTransfer !== 'undefined') {
        try {
          const transfer = new DataTransfer();
          transfer.items.add(savedFile);
          logoInput.files = transfer.files;
          showLogo(savedFile);
        } catch { logoFiles.delete(key); }
      }
      logoInput.addEventListener('change', () => {
        const file = logoInput.files?.[0];
        const problem = logoProblem(file);
        if (logoError) { logoError.textContent = problem; logoError.hidden = !problem; }
        if (!file || problem) { clearLogo(); return; }
        logoFiles.set(key, file);
        showLogo(file);
      }, opts);
      logoRemove?.addEventListener('click', () => {
        clearLogo();
        if (logoError) logoError.hidden = true;
        logoInput.focus();
      }, opts);
    }

    // Capture-phase guard: runs before the theme's add-to-cart handler for any
    // submit path that skips native validation.
    form?.addEventListener('submit', (event) => {
      const missing = fields.find((field) => field.required && !field.value.trim());
      if (certify.checked && !missing) {
        // Files can't travel through the background add-to-cart, so when a logo is
        // attached, send the form the classic way (multipart) to /cart/add.
        if (logoInput?.files?.length) {
          event.preventDefault();
          event.stopImmediatePropagation();
          form.enctype = 'multipart/form-data';
          const button = event.submitter;
          if (button instanceof HTMLElement) {
            button.setAttribute('aria-disabled', 'true');
            button.classList.add('opacity-50', 'cursor-wait');
          }
          HTMLFormElement.prototype.submit.call(form);
        }
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      if (missing) {
        missing.reportValidity();
        missing.focus();
        return;
      }
      if (error) error.hidden = false;
      certify.focus();
    }, { ...opts, capture: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }
}

if (!customElements.get('s-lftg-fields')) customElements.define('s-lftg-fields', LftgFields);
